import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { Plus, Trash2, MapPin, X, Crown, Building2, User, KeyRound, Pencil } from 'lucide-react';

// ── Add / Edit user dialog ─────────────────────────────────────────────────
function AddUserDialog({ onClose, onSave, api, facilities }) {
  const [form, setForm] = useState({ username: '', password: '', role: 'location_admin', assigned_locations: [] });
  const [saving, setSaving] = useState(false);

  const toggleLocation = (id) => {
    setForm(f => ({
      ...f,
      assigned_locations: f.assigned_locations.includes(id)
        ? f.assigned_locations.filter(l => l !== id)
        : [...f.assigned_locations, id],
    }));
  };

  const handleSave = async () => {
    if (!form.username || !form.password) { toast.error('Username and password required'); return; }
    setSaving(true);
    try {
      await api.post('/api/users/', form);
      toast.success('User created');
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const ROLE_CARDS = [
    {
      id: 'location_admin',
      label: 'Location Admin',
      icon: Building2,
      desc: 'Access limited to assigned locations. Can manage bookings and view calendar.',
    },
    {
      id: 'owner',
      label: 'Owner',
      icon: Crown,
      desc: 'Full access to all locations, facilities, users, and settings.',
    },
  ];

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 520 }}>
        <div className="sb-modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="sb-modal-title">Add Team Member</div>
            <div className="sb-modal-sub">New admin account for the operator console</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={16} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Live preview */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10 }}>
            <div className={`sb-av ${avatarClass(form.username || 'NU')}`} style={{ width: 36, height: 36, fontSize: 13 }}>
              {(form.username.trim().slice(0, 2) || 'NU').toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{form.username.trim() || 'New member'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                {form.role === 'owner'
                  ? 'Owner · All locations'
                  : `Location Admin · ${form.assigned_locations.length} location${form.assigned_locations.length !== 1 ? 's' : ''}`}
              </div>
            </div>
            <span className={`sb-badge ${form.role === 'owner' ? 'warn' : 'info'}`} style={{ marginLeft: 'auto' }}>
              {form.role === 'owner' ? 'Owner' : 'Location Admin'}
            </span>
          </div>

          <div className="sb-field">
            <label>Username</label>
            <input
              type="text"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              className="sb-form-input"
              placeholder="e.g. rahul_admin"
              autoComplete="off"
            />
          </div>
          <div className="sb-field">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="sb-form-input"
              placeholder="Minimum 6 characters"
              autoComplete="new-password"
            />
          </div>

          {/* Role radio cards */}
          <div>
            <span className="sb-form-label">Role</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {ROLE_CARDS.map(rc => {
                const Icon = rc.icon;
                const active = form.role === rc.id;
                return (
                  <button
                    key={rc.id}
                    type="button"
                    onClick={() => setForm({ ...form, role: rc.id })}
                    style={{
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: `1px solid ${active ? 'var(--brand)' : 'var(--line)'}`,
                      background: active ? 'color-mix(in oklch, var(--brand) 8%, var(--surface))' : 'var(--surface-2)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all .12s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <Icon size={14} style={{ color: active ? 'var(--brand)' : 'var(--ink-3)', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--ink)' : 'var(--ink-2)' }}>{rc.label}</span>
                    </div>
                    <p style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.4, margin: 0 }}>{rc.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Location assignment */}
          {form.role === 'location_admin' && (
            <div>
              <span className="sb-form-label">Assign Locations</span>
              <div className="sb-card" style={{ overflow: 'hidden' }}>
                {facilities.length === 0 ? (
                  <p style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                    No facilities configured yet.
                  </p>
                ) : facilities.map((f, fi) => (
                  <label
                    key={f.location_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      borderBottom: fi < facilities.length - 1 ? '1px solid var(--line-soft)' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.assigned_locations.includes(f.location_id)}
                      onChange={() => toggleLocation(f.location_id)}
                      style={{ accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, color: 'var(--ink)' }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto', fontFamily: 'var(--f-mono)' }}>
                      #{f.location_id}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn primary">
            {saving ? 'Creating…' : 'Create Member'}
          </button>
        </div>
      </div>
    </>
  );
}

function EditLocationsDialog({ user: targetUser, onClose, onSave, api, facilities }) {
  const [locs, setLocs] = useState(targetUser.assigned_locations || []);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setLocs(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/users/${targetUser.user_id}/locations`, { assigned_locations: locs });
      toast.success('Locations updated');
      onSave();
      onClose();
    } catch {
      toast.error('Failed to update locations');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 400 }}>
        <div className="sb-modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="sb-modal-title">Edit Locations</div>
            <div className="sb-modal-sub">{targetUser.username}</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={16} /></button>
        </div>
        <div className="sb-modal-body">
          <div className="sb-card" style={{ overflow: 'hidden' }}>
            {facilities.length === 0 ? (
              <p style={{ padding: '12px 16px', fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                No facilities configured yet.
              </p>
            ) : facilities.map((f, fi) => (
              <label
                key={f.location_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  borderBottom: fi < facilities.length - 1 ? '1px solid var(--line-soft)' : 'none',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={locs.includes(f.location_id)}
                  onChange={() => toggle(f.location_id)}
                  style={{ accentColor: 'var(--brand)', width: 14, height: 14, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: 'var(--ink)' }}>{f.name}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto', fontFamily: 'var(--f-mono)' }}>
                  #{f.location_id}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Edit username dialog ────────────────────────────────────────────────────
function EditUsernameDialog({ user: targetUser, isSelf, onClose, onSave, api }) {
  const [username, setUsername] = useState(targetUser.username || '');
  const [saving, setSaving] = useState(false);
  const trimmed = username.trim();
  const unchanged = trimmed === targetUser.username;

  const handleSave = async () => {
    if (trimmed.length < 3) { toast.error('Username must be at least 3 characters'); return; }
    if (unchanged) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/api/users/${targetUser.user_id}/username`, { username: trimmed });
      toast.success(`Renamed to ${trimmed}`);
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update username');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 400 }}>
        <div className="sb-modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="sb-modal-title">Edit Username</div>
            <div className="sb-modal-sub">{targetUser.username}{isSelf ? ' (you)' : ''}</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={16} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="sb-field">
            <label>Username</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="sb-form-input"
              placeholder="e.g. rahul_admin"
              autoComplete="off"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--ink-4)', margin: 0 }}>
            {isSelf
              ? 'Your current session keeps the old name until you sign in again with the new username.'
              : 'They’ll sign in with the new username from their next login; their current session is unaffected.'}
          </p>
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving || trimmed.length < 3} className="sb-btn primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Reset password dialog ──────────────────────────────────────────────────
function ResetPasswordDialog({ user: targetUser, isSelf, onClose, onSave, api }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/users/${targetUser.user_id}/password`, { password });
      toast.success(`Password reset for ${targetUser.username}`);
      onSave?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 400 }}>
        <div className="sb-modal-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="sb-modal-title">Reset Password</div>
            <div className="sb-modal-sub">{targetUser.username}{isSelf ? ' (you)' : ''}</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={16} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="sb-field">
            <label>New password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="sb-form-input"
              placeholder="Minimum 6 characters"
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <div className="sb-field">
            <label>Confirm password</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="sb-form-input"
              placeholder="Re-enter the new password"
              autoComplete="new-password"
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--ink-4)', margin: 0 }}>
            {isSelf
              ? 'Your current session stays signed in; use the new password next time you log in.'
              : 'This user’s active session stays valid until it expires; they’ll use the new password on their next login.'}
          </p>
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn primary">
            {saving ? 'Saving…' : 'Reset Password'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Avatar color hash ──────────────────────────────────────────────────────
function avatarClass(name = '') {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 5;
  return `c${h}`;
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function AdminUsers() {
  const { user, api } = useAuth();
  const { facilities } = useFacilities();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editLocUser, setEditLocUser] = useState(null);
  const [editNameUser, setEditNameUser] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchUsers = useCallback(() => {
    api.get('/api/users/')
      .then(res => setUsers(res.data))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (u) => {
    try {
      await api.delete(`/api/users/${u.user_id}`);
      toast.success('User deleted');
      setConfirmDeleteId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const locationName = (id) => facilities.find(f => f.location_id === id)?.name || `Loc #${id}`;

  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Configure</div>
          <h1>Team</h1>
          <div className="sb-sub">Owners and location admins with access to the operator console.</div>
        </div>
        <div className="sb-page-actions">
          <button onClick={() => setShowAdd(true)} className="sb-btn primary">
            <Plus size={13} /> Add member
          </button>
        </div>
      </div>

      {loading ? (
        <div className="sb-card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>
      ) : users.length === 0 ? (
        <div className="sb-card" style={{ padding: '64px 24px', textAlign: 'center' }}>
          <User size={36} style={{ color: 'var(--ink-4)', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--ink-2)', marginBottom: 12 }}>No team members yet</p>
          <button onClick={() => setShowAdd(true)} className="sb-btn brand">
            <Plus size={13} /> Add first member
          </button>
        </div>
      ) : (
        <div className="sb-card" style={{ overflow: 'hidden' }}>
          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 2fr 1fr auto',
            gap: 16,
            padding: '10px 20px 8px',
            borderBottom: '1px solid var(--line-soft)',
          }}>
            {['Member', 'Role', 'Locations', 'Since', ''].map((h, i) => (
              <div key={i} className="sb-card-lbl" style={{ margin: 0 }}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {users.map(u => (
            <div
              key={u.user_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 2fr 1fr auto',
                gap: 16,
                padding: '12px 20px',
                alignItems: 'center',
                borderBottom: '1px solid var(--line-soft)',
              }}
            >
              {/* Member */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  className={`sb-av ${avatarClass(u.username)}`}
                  style={{ width: 32, height: 32, fontSize: 12 }}
                >
                  {u.username.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-.01em' }}>
                    {u.username}
                    {u.user_id === user?.user_id && (
                      <span className="sb-badge" style={{ marginLeft: 8, fontSize: 10 }}>You</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Role */}
              <div>
                <span className={`sb-badge ${u.role === 'owner' ? 'warn' : 'info'}`}>
                  {u.role === 'owner' ? 'Owner' : 'Location Admin'}
                </span>
              </div>

              {/* Locations */}
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                {u.role === 'owner' ? (
                  <span style={{ color: 'var(--brand)', fontSize: 12 }}>All locations</span>
                ) : u.assigned_locations?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {u.assigned_locations.map(lid => (
                      <span
                        key={lid}
                        style={{
                          fontSize: 11,
                          padding: '2px 7px',
                          background: 'var(--surface-2)',
                          border: '1px solid var(--line)',
                          borderRadius: 6,
                          color: 'var(--ink-2)',
                        }}
                      >
                        {locationName(lid)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>None assigned</span>
                )}
              </div>

              {/* Since */}
              <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}>
                {u.created_at?.split('T')[0] || '—'}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                {/* Renaming the 'admin' account is limited to the 'admin' account. */}
                {(user?.username === 'admin' || u.username !== 'admin') && (
                  <button
                    onClick={() => setEditNameUser(u)}
                    className="sb-btn sm ghost"
                    title="Edit username"
                  >
                    <Pencil size={12} /> Rename
                  </button>
                )}
                {u.role === 'location_admin' && (
                  <button
                    onClick={() => setEditLocUser(u)}
                    className="sb-btn sm ghost"
                    title="Edit location access"
                  >
                    <MapPin size={12} /> Locations
                  </button>
                )}
                {/* Resetting another owner's password is limited to the 'admin' account. */}
                {(user?.username === 'admin' || u.role !== 'owner' || u.user_id === user?.user_id) && (
                  <button
                    onClick={() => setResetPwUser(u)}
                    className="sb-btn sm ghost"
                    title="Reset password"
                  >
                    <KeyRound size={12} /> Password
                  </button>
                )}
                {u.user_id !== user?.user_id && (
                  confirmDeleteId === u.user_id ? (
                    <>
                      <button onClick={() => setConfirmDeleteId(null)} className="sb-btn sm ghost">Cancel</button>
                      <button onClick={() => handleDelete(u)} className="sb-btn sm danger">Confirm</button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(u.user_id)}
                      className="sb-btn sm ghost"
                      style={{ color: 'var(--bad-fg)' }}
                      title="Delete user"
                    >
                      <Trash2 size={12} />
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddUserDialog
          api={api}
          facilities={facilities}
          onClose={() => setShowAdd(false)}
          onSave={fetchUsers}
        />
      )}
      {editLocUser && (
        <EditLocationsDialog
          user={editLocUser}
          api={api}
          facilities={facilities}
          onClose={() => setEditLocUser(null)}
          onSave={fetchUsers}
        />
      )}
      {resetPwUser && (
        <ResetPasswordDialog
          user={resetPwUser}
          isSelf={resetPwUser.user_id === user?.user_id}
          api={api}
          onClose={() => setResetPwUser(null)}
        />
      )}
      {editNameUser && (
        <EditUsernameDialog
          user={editNameUser}
          isSelf={editNameUser.user_id === user?.user_id}
          api={api}
          onClose={() => setEditNameUser(null)}
          onSave={fetchUsers}
        />
      )}
    </div>
  );
}
